import { useState } from "react";
import { AlertTriangle, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function AdminSystem() {
  const [confirm, setConfirm] = useState("");
  const [resetting, setResetting] = useState(false);
  const [result, setResult] = useState(null);

  const runReset = async () => {
    setResetting(true);
    setResult(null);
    try {
      const { data } = await api.post("/admin/data/reset", { confirm });
      setResult(data);
      setConfirm("");
      toast.success("Test data cleared");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setResetting(false);
    }
  };

  return (
    <div>
      <h2 className="font-heading text-2xl tracking-tight font-medium text-slate-900 mb-1">
        System
      </h2>
      <p className="text-sm text-slate-500 mb-6">
        Go-live tools for the primary administrator.
      </p>

      <div className="bg-white border border-red-200 rounded-md overflow-hidden max-w-2xl" data-testid="data-reset-card">
        <div className="bg-red-50 border-b border-red-200 px-6 py-4 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-red-700" />
          <h3 className="font-heading text-lg font-medium text-red-800">Danger Zone — Clean Slate</h3>
        </div>
        <div className="p-6">
          <p className="text-sm text-slate-600">
            This permanently deletes <strong>all filings, all card payment records, and all
            merchant &amp; staff accounts</strong> (except you, the primary administrator). Use this
            once, right before going live, to remove test data.
          </p>
          <p className="text-sm text-emerald-700 mt-3">
            Yearly archives are <strong>never deleted</strong> and will be preserved.
          </p>

          <div className="mt-5 space-y-2">
            <Label>
              Type <span className="font-mono-data text-red-700">DELETE ALL DATA</span> to confirm
            </Label>
            <Input
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="DELETE ALL DATA"
              data-testid="reset-confirm-input"
            />
          </div>

          <Button
            className="mt-4 bg-red-700 hover:bg-red-800 text-white"
            disabled={confirm !== "DELETE ALL DATA" || resetting}
            onClick={runReset}
            data-testid="reset-data-btn"
          >
            {resetting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
            Permanently clear test data
          </Button>

          {result && (
            <div className="mt-5 bg-slate-50 border border-slate-200 rounded-md p-4 text-sm text-slate-700" data-testid="reset-result">
              <p className="font-medium text-slate-900 mb-1">Done — {result.message}</p>
              <ul className="list-disc pl-5 space-y-0.5">
                <li>{result.filings_deleted} filing(s) deleted</li>
                <li>{result.payments_deleted} payment record(s) deleted</li>
                <li>{result.users_deleted} user account(s) deleted</li>
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
